import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import axios from 'axios';
import { decode } from 'js-base64';
import head from 'lodash/head';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import { path } from 'ramda';

import { KeycloakContext } from 'components/Secured';
import restEndpoints from 'constants/restEndpoints';
import { useThrottle } from 'hooks/useThrottle';
import IProducer from 'models/producer';
import IProduct from 'models/product';
import { toSafeFloat } from 'utils/helpers-ts';

// TODO: move
interface IImage {
  id?: string;
  imgSrc?: string;
  isExisting?: boolean;
  isNew?: boolean;
  pictureUrl?: string;
};

interface ICoopProductJsonProps {
  isProducerOrStore?: boolean;
  isSuperuser?: boolean;
  state?: IProduct;
  producer?: IProducer;
  images?: IImage[];
  producerData?: IProducer;
  isSubmitting?: boolean;
}

interface ICoopProductJson {
  data?: IProduct;
  error?: string;
}


// TODO: move
const parseNumber = (value: string|number|undefined|null) => {
  if (isNil(value)) return null;
  if (typeof value === 'string') {
    if (value.trim() === '') return null;
    return parseFloat(value.replace(',', '.'));
  }
  if (typeof value === 'number') {
    return value;
  }
  return null;
};

const parseIntValue = (value: string|number|undefined|null) => {
  if (isNil(value)) return null;
  if (typeof value === 'string') {
    if (value.trim() === '') return null;
    return parseInt(value.replace(',', '.'));
  }
  if (typeof value === 'number') {
    return value;
  }
  return null;
};

const useCoopExportData = ({ isProducerOrStore, isSuperuser, state, producer, images, producerData, isSubmitting }: ICoopProductJsonProps) => {
  const keycloakCtx = useContext(KeycloakContext);
  const token = path(['keycloak', 'token'], keycloakCtx);

  // Throttle state changes to prevent excessive API calls while typing
  const throttledState = useThrottle(state, 500);

  const existingImages = useMemo(() => images?.filter((x) => x.isExisting), [images]);
  const image = useMemo(() => head(existingImages)?.pictureUrl, [existingImages]);

  let disabled = true;
  if (isSuperuser) disabled = false;
  if (isProducerOrStore && (!producerData?.organizations || producerData?.organizations?.includes('coop'))) {
    disabled = false;
  }
  if (isSubmitting) disabled = true;

  const newState = useMemo(() => ({
    ...throttledState,
    other_substances: throttledState?.other_substances?.map((s: any) => ({ ...s, amount: parseNumber(s?.amount) })),
    created_at: parseIntValue(throttledState?.created_at),
    updated_at: parseIntValue(throttledState?.updated_at),
    // adminStatus: state?.adminStatus,
    // foodlaCategory: state?.foodlaCategory,
    // title: state?.title,
    image_src: image,
    creator: throttledState?.producerId ? decode(throttledState.producerId) : undefined,
    descriptive_size_amount: parseNumber(throttledState?.descriptive_size_amount),
    descriptive_size_unit: throttledState?.descriptive_size_unit && throttledState?.descriptive_size_unit.trim() !== '' ? throttledState?.descriptive_size_unit : null,
    // EAN: state?.EAN,
    // EAN2: state?.EAN2,
    // purchasingDataSupplierArticleNo: state?.purchasingDataSupplierArticleNo,
    // short_text: state?.short_text,
    // shortSalesDescription: state?.shortSalesDescription,
    // ingredient_statement: state?.ingredient_statement,
    // animalFoodIngredients: state?.animalFoodIngredients,
    // non_food_ingredients: state?.non_food_ingredients,
    // labels: state?.labels,
    // additionalCharacteristics: state?.additionalCharacteristics,
    // nutritional_unit: state?.nutritional_unit,
    energi_kj: parseNumber(throttledState?.energi_kj),
    energi_kcal: parseNumber(throttledState?.energi_kcal),
    fett: parseNumber(throttledState?.fett),
    mattatFett: parseNumber(throttledState?.mattatFett),
    enkelomattatFett: parseNumber(throttledState?.enkelomattatFett),
    flerromattatFett: parseNumber(throttledState?.flerromattatFett),
    kolhydrat: parseNumber(throttledState?.kolhydrat),
    sockerarter: parseNumber(throttledState?.sockerarter),
    polyoler: parseNumber(throttledState?.polyoler),
    starkelse: parseNumber(throttledState?.starkelse),
    fiber: parseNumber(throttledState?.fiber),
    protein: parseNumber(throttledState?.protein),
    salt: parseNumber(throttledState?.salt),
    vitaminA: parseNumber(throttledState?.vitaminA),  
    vitaminD: parseNumber(throttledState?.vitaminD),
    vitaminE: parseNumber(throttledState?.vitaminE),
    vitaminK: parseNumber(throttledState?.vitaminK),
    vitaminC: parseNumber(throttledState?.vitaminC),
    tiamin: parseNumber(throttledState?.tiamin),
    riboflavin: parseNumber(throttledState?.riboflavin),
    niacin: parseNumber(throttledState?.niacin),
    vitaminB6: parseNumber(throttledState?.vitaminB6),
    folsyra: parseNumber(throttledState?.folsyra),
    vitaminB12: parseNumber(throttledState?.vitaminB12),
    biotin: parseNumber(throttledState?.biotin),
    pantotensyra: parseNumber(throttledState?.pantotensyra),
    kalium: parseNumber(throttledState?.kalium),
    klorid: parseNumber(throttledState?.klorid),
    kalcium: parseNumber(throttledState?.kalcium),
    fosfor: parseNumber(throttledState?.fosfor),
    magnesium: parseNumber(throttledState?.magnesium),
    jarn: parseNumber(throttledState?.jarn),
    zink: parseNumber(throttledState?.zink),
    koppar: parseNumber(throttledState?.koppar),
    mangan: parseNumber(throttledState?.mangan),
    fluorid: parseNumber(throttledState?.fluorid),
    selen: parseNumber(throttledState?.selen),
    krom: parseNumber(throttledState?.krom),
    molybden: parseNumber(throttledState?.molybden),
    jod: parseNumber(throttledState?.jod),
    // package_size: parseNumber(state?.package_size),
    // min_temperature: state?.min_temperature,
    // max_temperature: state?.max_temperature,
    // trade_item_temperature_information: state?.trade_item_temperature_information,
    // place_of_item_activity: state?.place_of_item_activity,
    // fishProductionMethod: state?.fishProductionMethod,
    // fishCatchMethod: state?.fishCatchMethod,
    // catchArea: state?.catchArea,
    // eggWeightClass: state?.eggWeightClass,
    // eggPackerApprovalNumber: state?.eggPackerApprovalNumber,
    // country_of_manufacturing: state?.country_of_manufacturing,
    // search_terms: state?.search_terms,
    consumer_size_to_order_size_ratio: throttledState?.consumer_size_to_order_size_ratio ? toSafeFloat(throttledState?.consumer_size_to_order_size_ratio, 1) : undefined,
    gross_weight_num: parseNumber(throttledState?.gross_weight_num),
    gross_height: parseNumber(throttledState?.gross_height),
    gross_width: parseNumber(throttledState?.gross_width),
    gross_depth: parseNumber(throttledState?.gross_depth),
    // brand: state?.brand,
    // brand_food: state?.brand_food,
    // producer: {
    //   name: state?.producerName,
    //   username: state?.producerUsername,
    //   phone: state?.producerPhone,
    //   email: state?.producerEmail,
    // },
    // manufacturer: {
    //   gln: state?.manufacturerGln,
    // },
    // location: state?.location,
    // classification: state?.classification,
    alcoholPercentage: parseNumber(throttledState?.alcoholPercentage),
    // color: state?.color,
    // material: state?.material,
    // dimensions: state?.dimensions,
    // capacity: state?.capacity,
    // recyclingType: state?.recyclingType,
    // dosageAndUsageInstructions: state?.dosageAndUsageInstructions,
    // preparationInstructions: state?.preparationInstructions,
    // signalWords: state?.signalWords,
    // hazardStatements: state?.hazardStatements,
    // securityNotices: state?.securityNotices,
    // hazardSymbols: state?.hazardSymbols,
    // responsibleLabelingApprovalNumber: state?.responsibleLabelingApprovalNumber,
    // animalCategoriesForFeed: state?.animalCategoriesForFeed,
    // feedAdditives: state?.feedAdditives,
    // analyticalConstituentsFeed: state?.analyticalConstituentsFeed,
    // feedingInstructions: state?.feedingInstructions,
    // animalAgeGroup: state?.animalAgeGroup,
    // preparedForm: state?.preparedForm,
    // cmrSubstance: state?.cmrSubstance,
    // CMRSubstances: state?.CMRSubstances,
    // reachSubstance: state?.reachSubstance,
    // bestBeforeDate: state?.bestBeforeDate,
    // childrenUnder16: state?.childrenUnder16,
    // complianceEg12232009: state?.complianceEg12232009,
    // qrLabelingSwedish: state?.qrLabelingSwedish,
    // qrMedicalClaims: state?.qrMedicalClaims,
    // qrMarketingClaimsCosmetic: state?.qrMarketingClaimsCosmetic,
    // sunProtectionFactor: state?.sunProtectionFactor,
    // complianceSunscreenRegulation: state?.complianceSunscreenRegulation,
    // qrEfsaUlValues: state?.qrEfsaUlValues,
    // qrNutritionClaims: state?.qrNutritionClaims,
    // qrHealthClaims: state?.qrHealthClaims,
    // qrFoodLabeling: state?.qrFoodLabeling,
    // qrMisleadingSalesTerms: state?.qrMisleadingSalesTerms,
    // qrFoodHygiene: state?.qrFoodHygiene,
    // categoryWeightloss: state?.categoryWeightloss,
    // qrClassificationLabelingPackaging: state?.qrClassificationLabelingPackaging,
    // complianceReach19072006: state?.complianceReach19072006,
    // reachSubstancesAnnex19072006: state?.reachSubstancesAnnex19072006,
    // regulatedSubstance: state?.regulatedSubstance,
    // complianceBiocid: state?.complianceBiocid,
    // hazardousSubstancesOver1: state?.hazardousSubstancesOver1,
    // informationHazardousSubstances: state?.informationHazardousSubstances,
    // complianceProductSafety2004451: state?.complianceProductSafety2004451,
    // ceMarking: state?.ceMarking,
    // specifyCEStandards: state?.specifyCEStandards,
    // complianceBiocidalTreated: state?.complianceBiocidalTreated,
    // qrElectronic: state?.qrElectronic,
    // complianceToyDirective: state?.complianceToyDirective,
    // complianceMetalsSkinContact: state?.complianceMetalsSkinContact,
    // complianceMedicalTechProducts: state?.complianceMedicalTechProducts,
    // checkUniqueIdentification: state?.checkUniqueIdentification,
    // explanationNoUDI: state?.explanationNoUDI,
    // complianceHaccp: state?.complianceHaccp,
    // approvalDietFeed: state?.approvalDietFeed,
    // qrAdditivesInFeed: state?.qrAdditivesInFeed,
    // qrFeedLabeling: state?.qrFeedLabeling,
    // qrRelevantFeedCertifications: state?.qrRelevantFeedCertifications,
    // plasticTrayPackaging: state?.plasticTrayPackaging,
    basicDataTaxClassification: parseNumber(throttledState?.basicDataTaxClassification),
    // palletQtyOfNextLowerItem: state?.palletQtyOfNextLowerItem,
    basicDataAvailabilityDateFrom: parseNumber(throttledState?.basicDataAvailabilityDateFrom),
    // palletEANForStorePack: state?.palletEANForStorePack,
    // consumerPackOrderableUnit: state?.consumerPackOrderableUnit,
    // consumerPackDespatchUnit: state?.consumerPackDespatchUnit,
    // storePackOrderableUnit: state?.storePackOrderableUnit,
    // storePackEANForStorePack: state?.storePackEANForStorePack,
    producer,
  }), [throttledState, producer, image]);

  const [isLoading, setIsLoading] = useState(false);
  const [product, setProduct] = useState<IProduct|undefined>(undefined);
  const [error, setError] = useState<string|undefined>(undefined);
  const [reloadTrigger, setReloadTrigger] = useState(0);

  // Track the last state that was sent to API to prevent duplicate calls
  const lastFetchedStateRef = useRef<any>(null);
  const lastScheduledStateRef = useRef<any>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(false);
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isFirstFetchRef = useRef(true);

  const refetch = () => {
    lastFetchedStateRef.current = null;
    lastScheduledStateRef.current = null;
    setReloadTrigger(prev => prev + 1);
  };

  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // 1. Don't fetch if disabled, or if state or token is missing
    if (disabled || !newState || !token) {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
      setProduct(undefined);
      lastScheduledStateRef.current = null;
      return;
    }

    // 2. Check if the state has actually changed using deep comparison
    if (isEqual(lastFetchedStateRef.current, newState)) {
      console.log('Coop export: Skipping duplicate call (data unchanged)');
      return;
    }

    // 3. Check if we already have a pending fetch for this exact state
    // Do not clear the timeout if it is already scheduled for this state,
    // otherwise we cancel the pending fetch on subsequent renders!
    if (isEqual(lastScheduledStateRef.current, newState)) {
      console.log('Coop export: Skipping duplicate call (already scheduled)');
      return;
    }

    // 4. Since the state has changed/is new, clear any previous pending timeout
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }

    // 5. Store the state we're about to schedule BEFORE setTimeout to prevent duplicate scheduling
    lastScheduledStateRef.current = newState;

    // For first fetch on mount, trigger immediately without delay to show preview on load
    // For subsequent changes, use a short delay to debounce rapid changes
    const debounceDelay = isFirstFetchRef.current ? 0 : 300;

    console.log(`Coop export: Scheduling API call in ${debounceDelay}ms`);

    fetchTimeoutRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;

      console.log('Coop export: Making API call', isFirstFetchRef.current ? '(first)' : '(subsequent)');

      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Now that we're actually making the call, update lastFetchedStateRef
      lastFetchedStateRef.current = newState;
      isFirstFetchRef.current = false;

      setIsLoading(true);
      setError(undefined);

      const formData = new FormData();
      formData.append('product', JSON.stringify(newState));

      const controller = new AbortController();
      abortControllerRef.current = controller;

      axios.post<ICoopProductJson>(
        restEndpoints.coop,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            ContentType: 'multipart/form-data',
          },
          signal: controller.signal,
        }
      )
        .then(res => {
          if (!isMountedRef.current) return;
          console.log('!!!res', res?.data);
          if (res?.data?.data) {
            setProduct(res.data.data);
          } else {
            setProduct(undefined);
            setError('No data.');
          }
        })
        .catch((error) => {
          if (!isMountedRef.current) return;
          // Don't set error if request was aborted
          if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
            setError(error.message);
          }
        })
        .finally(() => {
          if (isMountedRef.current) {
            setIsLoading(false);
          }
        });
    }, debounceDelay);
  }, [disabled, newState, token, reloadTrigger]);

  return {
    isLoading,
    error,
    data: product,
    refetch,
  };
};

export default useCoopExportData;
